import { Link, usePageContent } from "@appiodev/xcore-client/xcore-ui";
import { Relation } from "@appiodev/xcore-core";
import { Box, Flex, Heading3, SimpleGrid } from "@xcorejs/ui";
import ProductCard from "design-system/robe/cards/Product";
import ProductOverviewCard from "design-system/robe/cards/ProductOverview";
import { FC } from "react";
import { useLocalizedRelation } from "utils/useLocalizedRelation";
import { Accessory, Product, ProductOverviewPage, ProductOverviewPageValues, Products } from "xcore/types";

import OverviewText from "./OverviewText";
import ProductFilters from "./ProductFilters";

const ProductOverviewView: FC = () => {
  const [{ values }, { value, stringify, localize }] = usePageContent<Products | ProductOverviewPage>();

  const items = useLocalizedRelation<Relation<ProductOverviewPage | Product | Accessory>[], "itemsRelation">(
    "itemsRelation",
    value(values),
    undefined!
  );

  const products = items?.filter(
    value(values.hideDiscontinued)
      ? i => (i.type === "product" || i.type === "accessory") && !localize(i.values.discontinuedReg) && !value(i.values.discontinuedGlob)
      : i => i.type === "product" || i.type === "accessory"
  ) as Product[] | Accessory[] ?? [];

  const pops: ProductOverviewPage[] = items?.filter(item => item.type === "productOverviewPage") as ProductOverviewPage[] ?? [];
  const tilesLayout = (value(values) as ProductOverviewPageValues).tilesLayout;

  return (
    <>
      {!value(values.hideFilters) && <ProductFilters />}

      <OverviewText />

      {"html" in values && stringify(values.html)!.length > 0 && (
        <Box mt={{ _: "4rem" }}>
          <div dangerouslySetInnerHTML={{ __html: stringify(values.html)! }}></div>
        </Box>
      )}

      <Flex flexDirection={localize(value(values.showPages)) ? "column" : "column-reverse"}>
        {items?.length > 0 && (
          <Box mt="6rem">
            {"featuredCategoriesHeading" in values && stringify(values.featuredCategoriesHeading)!.length > 0 &&
            <Heading3 textAlign="center" fontWeight={600} mb="3rem">{stringify(values.featuredCategoriesHeading)}</Heading3>}
            {"pagesHeading" in values && stringify(values.pagesHeading)!.length > 0 &&
            <Heading3 textAlign="center" fontWeight={600} mb="3rem">{stringify(values.pagesHeading)}</Heading3>}

            {(tilesLayout === "3" || tilesLayout === "4") && (
              <>
                <SimpleGrid
                  gap={{ _: "1.5rem", sm: "3rem" }}
                  columns={{ _: 2, sm: 4 }}
                  column="minmax(100px, 300px)"
                  width={{ _: "auto", lg: "100%" }}
                  marginBottom={{ _: "1.5rem", sm: "3rem" }}
                >
                  {tilesLayout === "3" ? pops.map(item => (
                    <Link key={item.id} content={item} variant="simple" overflow="hidden">
                      <ProductOverviewCard value={item} />
                    </Link>
                  )) : tilesLayout === "4" ? products.map(item => (
                    <Link key={item.id} content={item} variant="simple" overflow="hidden">
                      <ProductCard product={item} />
                    </Link>
                  )) : null}
                </SimpleGrid>

                <SimpleGrid
                  gap={{ _: "1.5rem", sm: "3rem" }}
                  columns={{ _: 2, sm: 4 }}
                  column="minmax(100px, 300px)"
                  width={{ _: "auto", lg: "100%" }}
                >
                  {tilesLayout === "4" && pops.map(item => (
                    <Link key={item.id} content={item} variant="simple" overflow="hidden">
                      <ProductOverviewCard value={item} />
                    </Link>
                  ))}
                  {tilesLayout === "3" && products.map(item => (
                    <Link key={item.id} content={item} variant="simple" overflow="hidden">
                      <ProductCard product={item} />
                    </Link>
                  ))}
                </SimpleGrid>
              </>
            )}

            {tilesLayout !== "3" && tilesLayout !== "4" && (
              <SimpleGrid
                gap={{ _: "1.5rem", sm: "3rem" }}
                columns={{ _: 2, sm: 4 }}
                column="minmax(100px, 300px)"
                width={{ _: "auto", lg: "100%" }}
              >
                {items.map((item) =>
                  item.type === "product" || item.type === "accessory"
                    ? (
                      <Link key={item.id} content={item} variant="simple" overflow="hidden">
                        <ProductCard product={item} />
                      </Link>
                    ) : tilesLayout === "2" ? (
                      <Link key={item.id} content={item} variant="simple" overflow="hidden">
                        <ProductCard pop={item} />
                      </Link>
                    )
                      : (
                        <Link key={item.id} content={item} variant="simple" overflow="hidden">
                          <ProductOverviewCard value={item} />
                        </Link>
                      )
                )}
              </SimpleGrid>
            )}
          </Box>
        )}
      </Flex>
    </>
  );
};

export default ProductOverviewView;
